// The static demo's stand-in for the per-case figures the process list sums on the server.
import { parseUtcDate } from "../../utils";
import type { Process, ProcessSource } from "../types";

export type ProcessObservation = {
  traceStart: string;
  traceEnd: string;
  userId: string;
  seconds: number;
  apps: string[];
  appSeconds?: Record<string, number>;
};

/**
 * Whether a case overlaps request bounds, decided as the API decides it: by the case's own span,
 * or for a case without step timestamps (a minimum-instant start), its recording's UTC day.
 */
export const caseInBounds = (
  source: Pick<ProcessSource, "traceStart" | "traceEnd" | "recordingDate">,
  bounds: { datetime_gte?: string; datetime_lte?: string },
) => {
  const [start, end] = source.traceStart.startsWith("-")
    ? [
        Date.parse(`${source.recordingDate}T00:00:00Z`),
        Date.parse(`${source.recordingDate}T23:59:59.999Z`),
      ]
    : [parseUtcDate(source.traceStart).getTime(), parseUtcDate(source.traceEnd).getTime()];
  return (
    (!bounds.datetime_gte || end >= Date.parse(bounds.datetime_gte)) &&
    (!bounds.datetime_lte || start <= Date.parse(bounds.datetime_lte))
  );
};

// Keep only the fields the dashboards need, not graphs, screenshots or descriptions.
export const processObservations = (process: Process): ProcessObservation[] => {
  const nodes = new Map(process.nodes.map(node => [node.id, node]));
  return process.instances.map(instance => ({
    traceStart: instance.source.traceStart,
    traceEnd: instance.source.traceEnd,
    userId: instance.source.userId,
    // This is the L4 instance's active duration, not traceEnd - traceStart (elapsed time).
    seconds: instance.source.avgDuration,
    appSeconds: instance.steps.reduce<Record<string, number>>((totals, step) => {
      for (const [app, seconds] of Object.entries(step.appSeconds ?? {})) {
        if (Number.isFinite(seconds) && seconds > 0) totals[app] = (totals[app] ?? 0) + seconds;
      }
      return totals;
    }, {}),
    apps: [
      ...new Set(
        instance.steps.flatMap(step => {
          const attributed = Object.entries(step.appSeconds ?? {})
            .filter(([, seconds]) => seconds > 0)
            .map(([app]) => app);
          const app = nodes.get(step.nodeId)?.app;
          return step.appSeconds !== undefined ? attributed : app ? [app] : [];
        }),
      ),
    ],
  }));
};
